import { computed, ref, Ref, watch } from 'vue';
import { IFormItem, ICountryItem, ILabel } from '../utils/interfaces';

const generateLabel = (label: string, value: string): ILabel => {
  return { label, value };
}

export const usePrivacySelection = (formItem : IFormItem, countryItem: Ref<ICountryItem | null>) => {
  const currentRole = ref<string>('');
  const currentDecision = computed(() => countryItem.value?.countryJourneyDecision);
  const isRoles = computed(() => currentDecision.value === 'ROLES');
  const isCta = computed(() => currentDecision.value === 'CTA');
  const isEnd = computed(() => currentDecision.value === 'END');

  const getRoleOptions = computed(() => {

    const roleOptions = [];
    if (countryItem.value?.consumerRoleShowRoleOption) {
      roleOptions.push(generateLabel(formItem.roleOptionConsumer, '0'));
    }

    if (countryItem.value?.businessRoleShowRoleOption) {
      roleOptions.push(generateLabel(formItem.roleOptionBusiness, '1'));
    }

    if (countryItem.value?.formerRoleShowRoleOption) {
      roleOptions.push(generateLabel(formItem.roleOptionFormer, '2'));
    }

    if (countryItem.value?.employeeRoleShowRoleOption) {
      roleOptions.push(generateLabel(formItem.roleOptionEmployee, '3'));
    }

    return roleOptions;
  });

  const getCurrentRoleIndex = computed(() => {
   return typeof currentRole.value === 'string' && currentRole.value !== '' ? parseInt(currentRole.value, 10) : -1;
  });

  const getSecondInfoRoleChoice = computed(() => {
    const roleChoices = [
      countryItem.value?.consumerRoleShowInformation,
      countryItem.value?.businessRoleShowInformation,
      countryItem.value?.formerRoleShowInformation,
      countryItem.value?.employeeRoleShowInformation
    ];
    return roleChoices[getCurrentRoleIndex.value];
  });

  const showSecondInfo = computed(() =>{
    if (isEnd.value || isCta.value) return false;
    return isRoles.value && getSecondInfoRoleChoice.value;
  });

  const getSecondInfo = computed(() =>{
    const secondPanelHtmlOptions = [
      countryItem.value?.consumerRoleInformation?.html,
      countryItem.value?.businessRoleInformation?.html,
      countryItem.value?.formerRoleInformation?.html,
      countryItem.value?.employeeRoleInformation?.html
    ];
    const secondPanelHtml = secondPanelHtmlOptions[getCurrentRoleIndex.value];
    return secondPanelHtml ?? '';
  });

  const getCtaRoleChoice = computed(() => {
    const roleChoices = [
      countryItem.value?.consumerRoleShowCTA,
      countryItem.value?.businessRoleShowCTA,
      countryItem.value?.formerRoleShowCTA,
      countryItem.value?.employeeRoleShowCTA,
    ]
    return roleChoices[getCurrentRoleIndex.value];
  });

  const getRolesCtaInfo = computed(() => {
    const urlChoices = [
      countryItem.value?.consumerRoleFormUrl,
      countryItem.value?.businessRoleFormUrl,
      countryItem.value?.formerRoleFormUrl,
      countryItem.value?.employeeRoleFormUrl
    ];

    const ctaChoices = [
      countryItem.value?.consumerRoleFormCTA,
      countryItem.value?.businessRoleFormCTA,
      countryItem.value?.formerRoleFormCTA,
      countryItem.value?.employeeRoleFormCTA
    ];

    const urlChoice = urlChoices[getCurrentRoleIndex.value] ?? '';
    const ctaChoice = ctaChoices[getCurrentRoleIndex.value] ?? '';

    return { urlChoice, ctaChoice };
  });

  const getCtaInfoFromNoRole = computed(() => {
    return {
      urlChoice: countryItem.value?.noRoleFormUrl ?? '',
      ctaChoice: countryItem.value?.noRoleFormCTA ?? ''
    };
  });

  const showCTA = computed(() => {
    return isCta.value || (isRoles.value && getCtaRoleChoice.value);
  });

  const getCTAInfo = computed(() => {
    if (isRoles.value)  return getRolesCtaInfo.value;
    if (isCta.value)  return getCtaInfoFromNoRole.value;
    return { urlChoice: '', ctaChoice: '' };
  });

  watch(countryItem, () => {
    currentRole.value = '';
  });

  return { currentRole,
           isRoles,
           getRoleOptions,
           showSecondInfo,
           getSecondInfo,
           showCTA,
           getCTAInfo
        };
};
